// Admin read API for client_error_events (Runtime Observability Phase 2).
// Reads through the admin-only list_client_errors SECDEF RPC — never queries
// the client_error_events table directly (that path is boundary-locked).

import type {SupabaseClient} from './supabaseTypes.js';

// Read-only, so it takes a signal. The same constraint newsletterApi states:
// `then` is required so a plain object fake still satisfies it, and the helper
// only reaches for `abortSignal` when the builder actually has one.
type MaybeAbortable<Builder> = {then: unknown; abortSignal?: (signal: AbortSignal) => Builder};

const withReadSignal = <Builder extends MaybeAbortable<Builder>>(builder: Builder, signal?: AbortSignal): Builder =>
  signal && builder && typeof builder.abortSignal === 'function' ? builder.abortSignal(signal) : builder;

// `before` is an ISO timestamp cursor; `p_before` is only sent when one is
// supplied, so the param object is built up rather than declared whole.
//
// `signal` exists for the route resource that owns /admin/client-errors: losing
// the last observer must cancel the request in flight, which is the half of the
// runtime contract that only holds when the reader forwards the signal. The
// Admin tab's panel passes none and is unchanged.
export type LoadClientErrorsOptions = {limit?: number; before?: string; signal?: AbortSignal};

// Rows stay `unknown`. The RPC's row shape lives in SQL and this repository has
// no generated database types, so naming an interface here would be an
// unverified assertion about the server rather than a fact — ClientErrorsView
// narrows the fields it renders itself.
export async function loadClientErrors(
  sb: SupabaseClient,
  {limit = 100, before, signal}: LoadClientErrorsOptions = {},
): Promise<unknown[]> {
  if (!sb) throw new Error('loadClientErrors: sb required');
  const params: {p_limit: number; p_before?: string} = {p_limit: limit};
  if (before) params.p_before = before;
  const {data, error} = await withReadSignal(sb.rpc('list_client_errors', params), signal);
  if (error) throw new Error(`loadClientErrors: ${error.message || String(error)}`);
  return data || [];
}
